---
title: "The Entire CLI: How It Works and Where It's Headed"
description: AI generated PRs are creating a massive code review bottleneck. Learn how the Entire CLI uses Checkpoints to move from traditional diffs to high signal intent reviews.
category: Engineering
author: Rizèl Scarlett
---

<!-- Editorial update notice, not a section title. -->
<!-- lint ignore no-emphasis-as-heading -->

_This blog was modified slightly on Aug 28, 2026._

"I don't even read the code my agents produce," is the ultimate developer flex of 2026.

But in reality, it's a bit of a coping mechanism. These days, there's simply too much code to review. Every experienced engineer knows what moving this quickly creates: hidden, high-interest debt. That's fine for a weekend vibe-coding project. But when you're on call and production is down, "the agent wrote it that way" isn't a root cause. Without a time machine, you end up debugging a black box, trying to decode based on diff alone.

A diff does not tell you what the developer asked for, which agent sessions contributed to the result, what alternatives were tried, or where the human changed the agent's output. That context matters when you need to review a change, debug it later, or understand how your team is using agents.

Checkpoints are Entire's way of keeping that context with the code.

## What's a Checkpoint?

![Anatomy of a Checkpoint](/blog/the-entire-cli-how-it-works-and-where-its-headed/anatomy_of_a_checkpoint_v3.svg)

A [Checkpoint](https://docs.entire.io/cli/checkpoints) is an addressable record of the work behind a code change. It includes the code state, the agent transcript, developer prompts, token usage, and line-level attribution showing what came from the agent and what was edited by the developer.

Each Checkpoint gets an ID stored in Git and linked to the commit with a trailer. That link lets you trace a committed change back to the session context that produced it.

When Checkpoints are pushed to a shared remote, teammates can inspect how a change was produced, rather than relying on the diff alone.

## How It Works

Developers are already accustomed to working with an agent and committing code. So we built the Entire CLI as an invisible layer capturing everything in the background.

That led to one strict constraint: all tools had to fit inside the existing Git workflow, from capturing sessions to storing metadata, to commits. The system is built on three core principles: designed for Git workflows, Git as a database, and a two-tier storage model.

### Designed for Git Workflows

A Checkpoint ID is stored in a commit trailer rather than derived from the commit hash. That means the connection between a commit and its session history can survive common history-rewriting operations such as rebase, amend, squash, and cherry-pick.

Checkpoint metadata is also stored separately from working branches. Moving or deleting a feature branch does not remove the permanent Checkpoint record. Because capture happens locally, an individual developer can use Checkpoints without requiring the whole team to change its CI setup or workflow.

### Git as a Database

Entire stores transcripts, prompts, attribution, summaries, and other Checkpoint data as Git objects rather than putting that history in a separate proprietary database. Teams can then choose where those objects live, opting for either the same repository or a separate one.

By default, Checkpoint metadata is stored separately from the working branches and travels with the repository. When the code and Checkpoints are pushed to a shared remote, other developers with access can retrieve that context too.

However, Checkpoint data can also live in a repository separate from the code, which is useful when the code is public but agent session data should remain private, or when a team wants to manage Checkpoint data separately from the source repository.

### A Two-Tier Storage Model

Entire needs to preserve useful states while an agent is still working without adding every intermediate step to the developer's normal commit history. So we store every Checkpoint as both temporary and permanent.

Before a commit, the CLI stores temporary Checkpoint data outside the working branch. This gives developers rewind points during a session without filling `git log` or pull requests with temporary commits.

![Pre-commit state showing shadow branches](/blog/the-entire-cli-how-it-works-and-where-its-headed/pre_commit_state_animated.gif)

Once the developer commits, Entire writes the relevant session context into a permanent Checkpoint and links it to the commit. The permanent record can include transcripts, prompts, file changes, token usage, and attribution.

![Post-commit state showing permanent checkpoints](/blog/the-entire-cli-how-it-works-and-where-its-headed/post_commit_state_animated.gif)

The underlying storage has evolved since the original launch, but the goal remains the same: keep temporary agent state out of the developer's normal history while preserving the context that matters once the work is committed.

## Where We're Headed

Once agent activity is captured as structured history, it can support more than rewind and attribution. We are building toward team visibility, search, auditability, and review that starts with the intent behind a change.

### Team Visibility

Git records who committed a change. Entire can add context about how that change was produced, including the agents involved, token usage, sessions, and attribution.

That gives teammates more information when they review or inherit agent-generated code. Instead of treating every commit as if it were produced the same way, they can inspect the work behind it when that context matters.

### Search

`git log` is useful for finding what changed. Checkpoint history can also make the reasoning behind a change searchable.

The goal is to let developers find the prompt that introduced a workaround, revisit the session behind a library choice, or recover the context behind an implementation decision without reconstructing it from the code months later.

### Audit and Transparency

Teams may need to know which parts of a change came from a developer and which came from an agent. That can matter for internal review, security, compliance, or incident investigation.

By retaining prompts, agent responses, attribution, and subsequent human edits with the code, Checkpoints provide a record teams can inspect when they need that provenance.

### From Code Review to Intent Review

Large agent-generated diffs make line-by-line review harder to scale. Checkpoints give reviewers another place to start: the intent behind the change and the history of how the implementation was produced. A reviewer can inspect the original prompt, the session history, and key decisions before deciding where the code itself needs closer attention.

The longer-term goal is review based on both the resulting code and the evidence behind it, rather than asking reviewers to reconstruct intent from the final diff alone.

---

At Entire, we're building this in the open. If you're experimenting with agents, join the [Discord](https://discord.gg/hsV7HTsk5p), read the [docs](https://docs.entire.io/introduction), or follow the project on [GitHub](https://github.com/entireio).
